/**
 * IOSInstallPrompt - Install prompt for iOS Safari users
 *
 * Shows instructions for adding the app to home screen on iOS
 * since iOS doesn't support the beforeinstallprompt event.
 *
 * Appears after 30 seconds on first visit, can be dismissed.
 */

import { useState, useEffect } from 'react'
import { isIOSSafari, isStandalone } from '@/utils/ios-detection'
import { X, Share, Plus, Home } from 'lucide-react'
import { Button } from '@/components/ui/button'

const IOS_PROMPT_DISMISSED_KEY = 'ios-install-prompt-dismissed'
const IOS_PROMPT_DELAY = 30000 // 30 seconds

export function IOSInstallPrompt() {
  const [isVisible, setIsVisible] = useState(false)

  useEffect(() => {
    // Check if should show prompt
    const shouldShow = () => {
      // Don't show if not iOS Safari
      if (!isIOSSafari()) return false

      // Don't show if already installed
      if (isStandalone()) return false

      // Don't show if previously dismissed
      const dismissed = localStorage.getItem(IOS_PROMPT_DISMISSED_KEY)
      if (dismissed) return false

      return true
    }

    if (!shouldShow()) return

    // Show after delay
    const timer = setTimeout(() => {
      setIsVisible(true)
    }, IOS_PROMPT_DELAY)

    return () => clearTimeout(timer)
  }, [])

  const handleDismiss = () => {
    setIsVisible(false)
    localStorage.setItem(IOS_PROMPT_DISMISSED_KEY, 'true')
  }

  if (!isVisible) return null

  return (
    <div className="fixed inset-x-0 bottom-0 z-50 p-4 pb-safe animate-in slide-in-from-bottom duration-500">
      <div className="mx-auto max-w-sm bg-background border-2 border-primary rounded-2xl shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="relative bg-gradient-to-r from-primary/10 to-primary/5 px-4 py-3 border-b">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="bg-primary/20 p-2 rounded-lg">
                <Home className="h-5 w-5 text-primary" />
              </div>
              <div>
                <h3 className="font-semibold text-sm">Install Brian 2</h3>
                <p className="text-xs text-muted-foreground">Add to Home Screen</p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleDismiss}
              className="h-8 w-8"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Instructions */}
        <div className="p-4 space-y-4">
          <p className="text-sm text-muted-foreground">
            Install Brian 2 for quick access and offline support:
          </p>

          <div className="space-y-3">
            {/* Step 1 */}
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                <span className="text-sm font-semibold text-primary">1</span>
              </div>
              <div className="flex-1">
                <p className="text-sm">
                  Tap the <Share className="inline h-4 w-4 mx-1 text-primary" /> <strong>Share</strong> button below
                </p>
              </div>
            </div>

            {/* Step 2 */}
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                <span className="text-sm font-semibold text-primary">2</span>
              </div>
              <div className="flex-1">
                <p className="text-sm">
                  Scroll and tap <Plus className="inline h-4 w-4 mx-1 text-primary" /> <strong>Add to Home Screen</strong>
                </p>
              </div>
            </div>

            {/* Step 3 */}
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                <span className="text-sm font-semibold text-primary">3</span>
              </div>
              <div className="flex-1">
                <p className="text-sm">
                  Tap <strong>Add</strong> to install
                </p>
              </div>
            </div>
          </div>

          {/* Animated arrow pointing to share button */}
          <div className="flex justify-center pt-2">
            <div className="animate-bounce">
              <svg
                className="h-8 w-8 text-primary"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 14l-7 7m0 0l-7-7m7 7V3"
                />
              </svg>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-4 pb-4">
          <Button
            variant="outline"
            className="w-full"
            onClick={handleDismiss}
          >
            Maybe Later
          </Button>
        </div>
      </div>
    </div>
  )
}
